import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const DashboardHome = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [targetFormat, setTargetFormat] = useState("docx");
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalConversions: 0,
    successRate: 0,
    filesProcessed: 0,
  });

  useEffect(() => {
    // Only fetch conversions if user is logged in
    if (user) {
      fetchConversions();
      calculateStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchConversions = async () => {
    try {
      const response = await api.get("/conversions");
      setConversions(response.data.data.conversions);
    } catch (error) {
      console.error("Error fetching conversions:", error);
    }
  };

  const calculateStats = () => {
    const total = conversions.length;
    const completed = conversions.filter(
      (c) => c.status === "completed"
    ).length;
    const successRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

    setStats({
      totalConversions: total,
      successRate: successRate,
      filesProcessed: completed,
    });
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("targetFormat", targetFormat);

    try {
      await api.post("/conversions/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      showNotification("success", "File uploaded successfully! Processing...");
      setFile(null);
      fetchConversions();
    } catch (error) {
      showNotification(
        "error",
        error.response?.data?.message || "Upload failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type, message) => {
    // Simple notification - you can replace this with a toast library
    const alertClass = type === "success" ? "alert-success" : "alert-danger";
    const alertDiv = document.createElement("div");
    alertDiv.className = `alert ${alertClass} dash-notification`;
    alertDiv.innerHTML = `<i class="fa fa-${
      type === "success" ? "check-circle" : "exclamation-circle"
    } me-2"></i>${message}`;
    document.body.appendChild(alertDiv);

    setTimeout(() => {
      alertDiv.classList.add("fade-out");
      setTimeout(() => alertDiv.remove(), 300);
    }, 3000);
  };

  // Allow public users to test the system
  const remainingCredits =
    user?.role === "admin" ? "∞" : user?.subscription?.extractionCredits || 0;
  const canConvert =
    !user ||
    user?.role === "admin" ||
    remainingCredits > 0 ||
    !user.freeTrialUsed;

  return (
    <div className="container-fluid px-0">
      {/* Stats Cards */}
      {user && (
        <div className="row g-3 g-md-4 mb-3 mb-md-4">
          <div className="col-12 col-sm-6 col-lg-6 col-xl-3">
            <div className="dash-stat-card primary">
              <div className="dash-stat-icon">
                <i className="fa fa-battery-three-quarters"></i>
              </div>
              <div className="dash-stat-content">
                <h6 className="dash-stat-label">Available Credits</h6>
                <h3 className="dash-stat-value">{remainingCredits}</h3>
                <p className="dash-stat-trend">
                  <i className="fa fa-arrow-up me-1"></i>
                  {user.subscription?.plan || "Free"} Plan
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-lg-6 col-xl-3">
            <div className="dash-stat-card success">
              <div className="dash-stat-icon">
                <i className="fa fa-file-o"></i>
              </div>
              <div className="dash-stat-content">
                <h6 className="dash-stat-label">Total Conversions</h6>
                <h3 className="dash-stat-value">
                  {stats.totalConversions}
                </h3>
                <p className="dash-stat-trend">
                  <i className="fa fa-check me-1"></i>
                  All time
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-lg-6 col-xl-3">
            <div className="dash-stat-card warning">
              <div className="dash-stat-icon">
                <i className="fa fa-check-circle"></i>
              </div>
              <div className="dash-stat-content">
                <h6 className="dash-stat-label">Success Rate</h6>
                <h3 className="dash-stat-value">{stats.successRate}%</h3>
                <p className="dash-stat-trend">
                  <i className="fa fa-line-chart me-1"></i>
                  {stats.filesProcessed} completed
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-lg-6 col-xl-3">
            <div className="dash-stat-card info">
              <div className="dash-stat-icon">
                <i className="fa fa-shield"></i>
              </div>
              <div className="dash-stat-content">
                <h6 className="dash-stat-label">Account Status</h6>
                <h3 className="dash-stat-value">
                  {user.role === "admin" ? "Admin" : "Active"}
                </h3>
                <p className="dash-stat-trend">
                  <i className="fa fa-circle text-success me-1"></i>
                  Verified
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Row */}
      <div className="row g-3 g-md-4">
        {/* File Upload Card */}
        <div className="col-12 col-xl-8 order-2 order-xl-1">
          <div className="dash-card">
            <div className="dash-card-header">
              <h5 className="dash-card-title">
                <i className="fa fa-upload me-2"></i>
                File Conversion
              </h5>
              <span className="dash-card-badge">
                <i className="fa fa-bolt me-1"></i>
                Quick Convert
              </span>
            </div>

            <div className="dash-card-body">
              {!user && (
                <div className="dash-alert info">
                  <i className="fa fa-info-circle me-2"></i>
                  <div>
                    <strong>Guest Mode:</strong> You can test file
                    conversion without an account.{" "}
                    <a href="/register" className="dash-alert-link">
                      Sign up
                    </a>{" "}
                    to track your conversions and get more features.
                  </div>
                </div>
              )}

              {user && !canConvert ? (
                <div className="dash-alert warning">
                  <i className="fa fa-exclamation-triangle me-2"></i>
                  <div>
                    <h5>No extraction credits remaining</h5>
                    <p>
                      Please upgrade your plan to continue converting files.
                    </p>
                    <a href="/dashboard/billing" className="btn dash-btn-primary">
                      <i className="fa fa-rocket me-1"></i>
                      Upgrade Plan
                    </a>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleFileUpload}
                  className="dash-upload-form"
                >
                  <div className="dash-form-group">
                    <label className="dash-form-label">
                      <i className="fa fa-paperclip me-2"></i>
                      Select File
                    </label>
                    <div className="dash-file-input-wrapper">
                      <input
                        type="file"
                        className="dash-file-input"
                        accept=".pdf,.docx"
                        onChange={(e) => setFile(e.target.files[0])}
                        required
                        id="fileInput"
                      />
                      <label
                        htmlFor="fileInput"
                        className="dash-file-label"
                      >
                        <i className="fa fa-cloud-upload"></i>
                        <span className="dash-file-text">
                          {file
                            ? file.name
                            : "Choose a file or drag it here"}
                        </span>
                        <span className="dash-file-subtext">
                          Supported: PDF, DOCX (Max 10MB)
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="dash-form-group">
                    <label className="dash-form-label">
                      <i className="fa fa-exchange me-2"></i>
                      Convert To
                    </label>
                    <select
                      className="dash-select"
                      value={targetFormat}
                      onChange={(e) => setTargetFormat(e.target.value)}
                    >
                      <option value="docx">
                        <i className="fa fa-file-word-o"></i> PDF to DOCX
                      </option>
                      <option value="pdf">
                        <i className="fa fa-file-pdf-o"></i> DOCX to PDF
                      </option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="btn dash-btn-primary dash-btn-lg"
                    disabled={loading || !file}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-magic me-2"></i>
                        Convert File
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Conversion History */}
          <div className="dash-card mt-3 mt-md-4">
            <div className="dash-card-header">
              <h5 className="dash-card-title">
                <i className="fa fa-history me-2"></i>
                Conversion History
              </h5>
              <button className="dash-card-action">
                <i className="fa fa-refresh me-1"></i>
                Refresh
              </button>
            </div>

            <div className="dash-card-body p-0">
              {conversions.length === 0 ? (
                <div className="dash-empty-state">
                  <i className="fa fa-folder-open-o"></i>
                  <h6>No conversions yet</h6>
                  <p>Upload your first file to get started</p>
                </div>
              ) : (
                <div className="dash-table-wrapper table-responsive">
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Conversion</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversions.map((conv) => (
                        <tr key={conv._id}>
                          <td>
                            <div className="dash-table-file">
                              <i className="fa fa-file-o me-2"></i>
                              <span>{conv.originalFilename}</span>
                            </div>
                          </td>
                          <td>
                            <span className="dash-conversion-badge">
                              {conv.originalFormat}{" "}
                              <i className="fa fa-arrow-right"></i>{" "}
                              {conv.targetFormat}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`dash-status-badge ${
                                conv.status === "completed"
                                  ? "success"
                                  : conv.status === "processing"
                                  ? "warning"
                                  : conv.status === "failed"
                                  ? "danger"
                                  : "secondary"
                              }`}
                            >
                              <i
                                className={`fa fa-${
                                  conv.status === "completed"
                                    ? "check-circle"
                                    : conv.status === "processing"
                                    ? "spinner fa-spin"
                                    : "times-circle"
                                } me-1`}
                              ></i>
                              {conv.status}
                            </span>
                          </td>
                          <td className="text-muted">
                            {new Date(conv.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }
                            )}
                          </td>
                          <td>
                            <button
                              className="dash-icon-btn"
                              title="Download"
                            >
                              <i className="fa fa-download"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="col-12 col-xl-4 order-1 order-xl-2 mb-3 mb-xl-0">
          {user ? (
            <>
              {/* Account Summary */}
              <div className="dash-card">
                <div className="dash-card-header">
                  <h5 className="dash-card-title">
                    <i className="fa fa-user me-2"></i>
                    Account Summary
                  </h5>
                </div>
                <div className="dash-card-body">
                  <div className="dash-info-item">
                    <span className="dash-info-label">Email</span>
                    <span className="dash-info-value">{user.email}</span>
                  </div>
                  <div className="dash-info-item">
                    <span className="dash-info-label">Plan</span>
                    <span
                      className={`dash-plan-badge ${user.subscription?.plan}`}
                    >
                      {user.subscription?.plan || "Free"}
                    </span>
                  </div>
                  <div className="dash-info-item">
                    <span className="dash-info-label">Credits</span>
                    <span className="dash-info-value">
                      {remainingCredits}
                    </span>
                  </div>
                  <div className="dash-info-item">
                    <span className="dash-info-label">Trial Status</span>
                    <span className="dash-info-value">
                      {user.freeTrialUsed ? (
                        <span className="text-muted">
                          <i className="fa fa-check-circle me-1"></i>
                          Used
                        </span>
                      ) : (
                        <span className="text-success">
                          <i className="fa fa-gift me-1"></i>
                          Available
                        </span>
                      )}
                    </span>
                  </div>

                  {user.subscription?.plan === "free" && (
                    <a
                      href="/dashboard/billing"
                      className="btn dash-btn-success w-100 mt-3"
                    >
                      <i className="fa fa-rocket me-1"></i>
                      Upgrade Plan
                    </a>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="dash-card mt-3 mt-md-4">
                <div className="dash-card-header">
                  <h5 className="dash-card-title">
                    <i className="fa fa-bolt me-2"></i>
                    Quick Actions
                  </h5>
                </div>
                <div className="dash-card-body">
                  <a href="/dashboard/files" className="dash-quick-action">
                    <i className="fa fa-folder-open"></i>
                    <span>View All Files</span>
                  </a>
                  <a href="/dashboard/usage" className="dash-quick-action">
                    <i className="fa fa-line-chart"></i>
                    <span>Usage Analytics</span>
                  </a>
                  <a href="/dashboard/billing" className="dash-quick-action">
                    <i className="fa fa-credit-card"></i>
                    <span>Manage Billing</span>
                  </a>
                </div>
              </div>
            </>
          ) : (
            /* Guest CTA */
            <div className="dash-card">
              <div className="dash-card-body text-center">
                <i className="fa fa-rocket dash-cta-icon-large"></i>
                <h5 className="mt-3 mb-2">Create an Account</h5>
                <p className="text-muted mb-3">
                  Sign up to unlock additional features:
                </p>
                <ul className="dash-feature-list">
                  <li>
                    <i className="fa fa-check"></i>
                    Track conversion history
                  </li>
                  <li>
                    <i className="fa fa-check"></i>
                    Manage your files
                  </li>
                  <li>
                    <i className="fa fa-check"></i>
                    Access API endpoints
                  </li>
                  <li>
                    <i className="fa fa-check"></i>
                    Get monthly credits
                  </li>
                </ul>
                <a
                  href="/register"
                  className="btn dash-btn-primary w-100 mb-2"
                >
                  <i className="fa fa-rocket me-1"></i>
                  Sign Up Free
                </a>
                <a href="/login" className="btn dash-btn-outline w-100">
                  Already have an account?
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
